<template>
  <div class="order-survey">
    <div class="d-flex align-center mb-1">
      <v-icon small class="mr-1">mdi-clipboard-check-outline</v-icon>
      <span class="font-weight-medium">{{ $t('onboarding.commissioning.survey.title') }}</span>
      <v-spacer />
      <v-chip v-if="outcome" x-small :color="outcome === 'feasible' ? 'green' : 'error'" outlined>
        {{ $t(`onboarding.commissioning.survey.outcome.${outcome}`) }}
      </v-chip>
    </div>

    <!-- Rendue : comment, quand, et ce que la visite a dit — le compte rendu se lit aussi quand c'est faisable. -->
    <template v-if="outcome">
      <div class="text-caption text--secondary">
        {{ $t(`onboarding.commissioning.survey.mode.${order.survey.mode}`) }}<template v-if="order.survey.scheduledAt"> · {{ order.survey.scheduledAt | formatDate }}</template>
      </div>
      <div v-if="order.survey.notes" class="order-survey__notes mt-1">{{ order.survey.notes }}</div>
      <div v-else class="text-caption text--disabled mt-1">{{ $t('onboarding.commissioning.survey.no-notes') }}</div>
    </template>

    <template v-else-if="order.survey || editing">
      <v-radio-group v-model="mode" row dense hide-details class="mt-0 mb-2" :disabled="!editable">
        <v-radio value="video" :label="$t('onboarding.commissioning.survey.mode.video')" />
        <v-radio value="on-site" :label="$t('onboarding.commissioning.survey.mode.on-site')" />
      </v-radio-group>
      <v-text-field v-model="scheduledAt" type="datetime-local" :label="$t('onboarding.commissioning.survey.date')" :disabled="!editable"
                    :hint="$t('onboarding.commissioning.survey.date-hint')" persistent-hint outlined dense class="mb-2" />
      <div class="d-flex flex-wrap" style="gap: 6px">
        <v-btn small color="primary" :loading="working === 'plan'" :disabled="!editable || !mode || !planChanged" @click="plan">{{ $t('save') }}</v-btn>
        <v-btn v-if="order.survey" small text :disabled="!editable || !!working" @click="drop">{{ $t('onboarding.commissioning.survey.drop') }}</v-btn>
        <v-btn v-else small text :disabled="!!working" @click="editing = false">{{ $t('cancel') }}</v-btn>
      </div>

      <!-- Rendre la visite : le compte rendu, exigé seulement quand ce n'est pas faisable (la commande se clôt). -->
      <template v-if="order.survey && editable">
        <v-textarea v-model="notes" :label="$t('onboarding.commissioning.survey.notes')" :hint="$t('onboarding.commissioning.survey.notes-hint')"
                    persistent-hint rows="2" outlined dense auto-grow class="mt-4" />
        <div class="d-flex flex-wrap mt-2" style="gap: 6px">
          <v-spacer />
          <v-btn small outlined color="error" :disabled="!notes.trim() || !!working" :loading="working === 'not-feasible'" @click="record(false)">
            {{ $t('onboarding.commissioning.survey.not-feasible') }}
          </v-btn>
          <v-btn small color="success" :disabled="!!working" :loading="working === 'feasible'" @click="record(true)">{{ $t('onboarding.commissioning.survey.feasible') }}</v-btn>
        </div>
      </template>
    </template>

    <v-btn v-else-if="editable" small text color="primary" class="px-0" @click="editing = true">
      <v-icon left small>mdi-plus</v-icon>{{ $t('onboarding.commissioning.survey.plan') }}
    </v-btn>
    <div v-else class="text-caption text--secondary">{{ $t('onboarding.commissioning.survey.none') }}</div>
  </div>
</template>

<script>
import dayjs from 'dayjs'
import CommissioningRepository from '@repository/CommissioningRepository'
import CommissioningMixin from '../CommissioningMixin'

/**
 * La visite technique d'une pose : la prévoir (en visio ou sur place, et quand), puis la rendre — faisable, la pose se
 * planifie ; pas faisable, la commande se clôt avec le compte rendu. La même sur la fiche et dans le tiroir de la file.
 */
export default {
  name: 'OrderSurveyPanel',

  mixins: [CommissioningMixin],

  props: {
    order: { type: Object, required: true },
  },

  data() {
    return { editing: false, mode: 'video', scheduledAt: '', notes: '', working: null }
  },

  computed: {
    outcome() {
      return this.order.survey?.outcome || null
    },

    editable() {
      return this.mayFollowOrders && this.order.status === 'open'
    },

    /** Le champ date-heure du navigateur parle en heure locale, sans fuseau. */
    initialScheduledAt() {
      return this.order.survey?.scheduledAt ? dayjs(this.order.survey.scheduledAt).format('YYYY-MM-DDTHH:mm') : ''
    },

    planChanged() {
      return !this.order.survey || this.mode !== this.order.survey.mode || this.scheduledAt !== this.initialScheduledAt
    },
  },

  watch: {
    order: {
      immediate: true,
      handler() {
        this.editing = false
        this.mode = this.order.survey?.mode || 'video'
        this.scheduledAt = this.initialScheduledAt
      },
    },
  },

  methods: {
    plan() {
      return this.run('plan', () => CommissioningRepository.planSurvey(this.order.id, this.mode, this.scheduledAt ? dayjs(this.scheduledAt).format() : null))
    },

    drop() {
      return this.run('drop', () => CommissioningRepository.planSurvey(this.order.id, null, null))
    },

    async record(feasible) {
      if (!feasible && !(await this.$dialog.confirm({
        title: this.$t('onboarding.commissioning.survey.not-feasible'),
        text: this.$t('onboarding.commissioning.survey.not-feasible-confirm'),
      }))) return
      const notes = this.notes.trim() || null
      if (await this.run(feasible ? 'feasible' : 'not-feasible', () => CommissioningRepository.recordSurvey(this.order.id, feasible, notes))) this.notes = ''
    },

    async run(what, call) {
      this.working = what
      try {
        this.$emit('updated', await call())
        return true
      } catch (e) {
        this.notifyError(e)
        return false
      } finally {
        this.working = null
      }
    },
  },
}
</script>

<style scoped>
.order-survey {
  border: 1px solid rgba(0, 0, 0, 0.1);
  border-radius: 8px;
  padding: 10px 12px;
}
.order-survey__notes {
  white-space: pre-line;
  font-size: 13px;
}
</style>
